import React from 'react';
import { VictoryBar, VictoryGroup, VictoryChart, VictoryAxis, LineSegment, VictoryStack, VictoryTooltip, VictoryContainer, VictoryLegend } from 'victory'
import { getMonthName, groupAndSubgroupBy, getMonthTickValues } from './utils';
import * as Constants from './constants';

export const MonthlySectionDataChartStacked = ({ rawData, yPropertyName, yAxisLabel }) => {
    if (!rawData) {
        return <p>Select a section to see its chart.</p>
    }

    if (!rawData.length) {
        return <p>No data found for selected section.</p>
    }

    // Determine range of dates included in the data so we can set the chart domain.
    // Grouping stacked charts is bugged in Victory as of 8/2019 so we need to cover the whole domain of "MM-YYYY"
    // and label individual ticks, rather than group by month as we do with the non-stacked charts.
    const xTickValues = getMonthTickValues(rawData);

    // Group the data by year and product type in order to form a stacked bar chart with data for each PT by month.
    const ungroupedData = rawData.map(d => ({
        y: d[yPropertyName],
        year: d.year,
        x: `${getMonthName(d.month)}-${d.year}`,
        productTypeName: d.productTypeName
    }));
    const yearToProductTypes = groupAndSubgroupBy(ungroupedData, "year", "productTypeName");
    const chartWidth = Constants.CHART_WIDTH;
    const chartHeight = Constants.CHART_HEIGHT;
    const stacks = Object.values(yearToProductTypes).map(productTypesToData =>
        <VictoryStack
            key={productTypesToData}
            categories={{ x: xTickValues }}>
            {
                Object.values(productTypesToData).map(productTypeData =>
                    <VictoryBar
                        key={`${productTypeData.productTypeName}::${productTypeData[0].monthAndYear}`}
                        data={productTypeData}
                        labels={d => d.y > 0 ? `${d.productTypeName}: ${d.y} ${yAxisLabel}` : null}
                        labelComponent={<VictoryTooltip />}
                        style={{
                            labels: {
                                fontSize: Constants.DATA_LABEL_FONT_SIZE,
                                verticalAnchor: "middle"
                            }
                        }}
                    />)
            }
        </VictoryStack>);

    const legendData = Array.from(
        new Set(rawData.map(d => d.productTypeName)))
        .map(productType => { return { name: productType } });

    // Compose the charts so they share an axis
    return (
        <div>
            <VictoryChart
                width={chartWidth}
                height={chartHeight}
                domainPadding={{ x: 30 }}
                padding={{ top: 5, right: 16, bottom: 80, left: 70 }}
            >
                <VictoryAxis crossAxis
                    label="Month"
                    style={{
                        tickLabels: {
                            fontSize: Constants.DATA_LABEL_FONT_SIZE,
                            padding: 2,
                            angle: -90,
                            textAnchor: "end",
                            verticalAnchor: "middle"
                        },
                        axisLabel: {
                            fontSize: Constants.AXIS_LABEL_FONT_SIZE,
                            padding: 65
                        }
                    }} />
                <VictoryAxis dependentAxis
                    label={yAxisLabel}
                    style={{
                        tickLabels: {
                            fontSize: Constants.DATA_LABEL_FONT_SIZE
                        },
                        axisLabel: {
                            fontSize: Constants.AXIS_LABEL_FONT_SIZE,
                            padding: 40
                        }
                    }}
                    gridComponent={
                        <LineSegment
                            type={"grid"}
                            style={{
                                stroke: "grey",
                                "strokeWidth": 0.5,
                            }}
                        />
                    }
                />
                <VictoryGroup
                    colorScale="qualitative"
                    style={{
                        data: { width: 10 }
                    }}>
                    {stacks}
                </VictoryGroup>
            </VictoryChart>
            <VictoryLegend
                x={64}
                containerComponent={
                    <VictoryContainer
                        width={Constants.CHART_WIDTH}
                        height={30 + legendData.length * 30}
                    />}
                title="Product Type"
                symbolSpacer={11}
                colorScale="qualitative"
                data={legendData}
                style={{
                    labels: { fontSize: Constants.DATA_LABEL_FONT_SIZE },
                    title: { fontSize: Constants.AXIS_LABEL_FONT_SIZE }
                }} />
        </div >
    );
}